import { useEffect, useRef, useCallback } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

// Route depth mapping - higher number = deeper in navigation hierarchy
// Note: /habit/:id now redirects to /?habit=id (drawer), so no entry needed
const ROUTE_DEPTH: Record<string, number> = {
  // Main tabs (level 0)
  '/': 0,
  '/challenges': 0,
  '/community': 0,
  '/shop': 0,
  '/settings': 0,
  
  // Second level pages (level 1)
  '/profile': 1,
}

/**
 * Get the depth of a route path
 * Dynamic routes like /habit/:id are mapped by their prefix
 */
function getRouteDepth(pathname: string): number {
  // Check exact match first
  if (ROUTE_DEPTH[pathname] !== undefined) {
    return ROUTE_DEPTH[pathname]
  }
  
  // Check prefix matches for dynamic routes
  // Note: /habit/:id/edit now redirects to ?editHabit=id (drawer), so no entry needed
  if (pathname.startsWith('/u/')) {
    return 2
  }
  
  // Default to level 1 for unknown routes
  return 1
}

export type NavigationDirection = 'forward' | 'back' | 'none'

interface NavigationDirectionState {
  direction: NavigationDirection
  previousPath: string | null
}

/**
 * Hook to track navigation direction (forward/back)
 * Uses route depth and navigation type to determine direction
 */
export function useNavigationDirection(): NavigationDirectionState {
  const location = useLocation()
  const navigationType = useNavigationType()
  const previousPathRef = useRef<string | null>(null)
  const directionRef = useRef<NavigationDirection>('none')
  
  useEffect(() => {
    const currentPath = location.pathname
    const previousPath = previousPathRef.current
    
    if (!previousPath) {
      // First load
      directionRef.current = 'none'
    } else if (navigationType === 'POP') {
      // Browser back/forward button
      directionRef.current = 'back'
    } else {
      // Compare route depths
      const currentDepth = getRouteDepth(currentPath)
      const previousDepth = getRouteDepth(previousPath)
      
      if (currentDepth > previousDepth) {
        directionRef.current = 'forward'
      } else if (currentDepth < previousDepth) {
        directionRef.current = 'back'
      } else {
        // Same level - check if it's a tab switch
        const mainTabs = ['/', '/challenges', '/community', '/shop', '/settings']
        const isTabSwitch = mainTabs.includes(currentPath) && mainTabs.includes(previousPath)
        
        if (isTabSwitch) {
          // For tab switches, use tab order to determine direction
          const currentIndex = mainTabs.indexOf(currentPath)
          const previousIndex = mainTabs.indexOf(previousPath)
          directionRef.current = currentIndex > previousIndex ? 'forward' : 'back'
        } else {
          directionRef.current = 'forward'
        }
      }
    }
    
    previousPathRef.current = currentPath
  }, [location.pathname, navigationType])
  
  return {
    direction: directionRef.current,
    previousPath: previousPathRef.current,
  }
}

/**
 * Hook to manually set navigation direction for specific transitions
 * Useful when you know the direction ahead of time
 */
export function useSetNavigationDirection() {
  const setDirection = useCallback((direction: NavigationDirection) => {
    // Store in session storage for the next navigation
    sessionStorage.setItem('miac-nav-direction-override', direction)
  }, [])
  
  const clearDirection = useCallback(() => {
    sessionStorage.removeItem('miac-nav-direction-override')
  }, [])
  
  return { setDirection, clearDirection }
}
